import React, { useMemo } from "react";
import { Edit2, ArrowUpRight, Trash2, Eye } from "lucide-react";
import { DataTable, Avatar, TableActionButton } from "@/components/ui";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import { MANAGE_STAGES_TEXT } from "@/constants/globalConstants";
import { StageDropdown } from "../../../verve-leads/leads/components/StageDropdown";

export const SiteVisitTableView = ({
  siteVisits,
  isLoading,
  onSiteVisitClick,
  onEditClick,
  onDeleteClick,
  onViewClick
}) => {
  const columns = useMemo(() => {
    const cols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "170px",
        render: (l) => (
          <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
            {l.customId || "—"}
          </span>
        )
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
          return (
            <div className="flex items-center gap-2.5">
              <Avatar name={fullName} size="xs" />
              <div className="flex flex-col min-w-0">
                <span className="text-brand-dark tracking-tight text-sm leading-tight truncate">
                  {fullName}
                </span>
                <span className="text-[10px] text-slate-400 truncate flex items-center gap-1 mt-0.5  tracking-tighter">
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: "Last Note",
        key: "lastNote",
        className: "min-w-[250px]",
        render: (l) => {
          let displayNote = l.stageNotes?.site_visit || l.inspectionNote || "";
          if (displayNote && typeof displayNote === 'string') {
            if (displayNote.includes(' | Note: ')) {
              displayNote = displayNote.split(' | Note: ')[1].trim();
            } else if (
              displayNote.startsWith('Lead Status changed') ||
              displayNote.startsWith('Design Status changed') ||
              displayNote.startsWith('Appointment scheduled') ||
              displayNote.startsWith('Lead assigned') ||
              displayNote.startsWith('Lead reassigned')
            ) {
              displayNote = "";
            }
          }
          return (
            <span className="text-[11px] text-slate-600 italic block leading-normal py-1">
              {displayNote || "—"}
            </span>
          );
        }
      },
      {
        header: "Rooms",
        key: "rooms",
        render: (l) => {
          // Check if roomCount is provided by the backend or fallback to the array length
          const count = l.siteVisitData?.roomCount ?? (l.siteVisitData?.rooms?.length || 0);
          return (
            <span className="text-[11px] font-medium text-slate-600">
              {count > 0 ? count : "—"}
            </span>
          );
        }
      },
      {
        header: "Actions",
        key: "actions",
        render: (l) => (
          <div className="flex items-center gap-2 justify-end">
            <TableActionButton
              icon={Eye}
              variant="secondary"
              onClick={(e) => { e.stopPropagation(); onViewClick && onViewClick(l); }}
              title="View Site Visit Details"
            />
          </div>
        )
      }
    ];

    return cols;
  }, [onSiteVisitClick, onEditClick, onDeleteClick, onViewClick]);
  return (
    <DataTable
      columns={columns}
      data={siteVisits}
      isLoading={isLoading}
      getRowClassName={(idx, row) => row?.isDisabled ? "opacity-50 bg-slate-50/50" : ""}
      className="border-none shadow-none rounded-none"
    />
  );
};


